import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useEditingTableCellName } from 'af-root/hooks/useEditingTableCellName';
import { createValidFromGlyph } from 'af-root/scenes/Company/Jobs/Preview/Materials/JobMaterialsTable/JobMaterialsTableForm/createValidFromGlyph';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';

import MaterialRateSource from 'af-components/MaterialRateSource';
import { DisplayCell } from 'af-components/RHFTableCellComponents/Input/DisplayCell';
import type { GlyphItem } from 'af-components/RHFTableCellComponents/Input/Glyph';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';

import type { MaterialsFormInput, MaterialsFormOutput } from '../formModel';

type Rate = 'shippingMarkup' | 'salesMarkup' | 'tax';

type CompanyRate = 'companyShippingMarkup' | 'companySalesMarkup' | 'companyTax';

const RATE_TO_COMPANY_RATE_VALUE: { [rate in Rate]: CompanyRate } = {
	shippingMarkup: 'companyShippingMarkup',
	salesMarkup: 'companySalesMarkup',
	tax: 'companyTax',
};

interface RateData {
	shippingMarkup: Nullable<number>;
	salesMarkup: Nullable<number>;
	tax: Nullable<number>;
	companyShippingMarkup: number;
	companySalesMarkup: number;
	companyTax: number;
}

interface OwnProps {
	rate: Rate;
	rateData: RateData;
	containerClassName: string;
	fieldsLength: number;
	readonly: boolean;
	displayOnly: boolean;
	rhfId: string;
	name: `materials.${number}.futureRate.${Rate}` | `materials.${number}.currentRate.${Rate}`;
	/** DB_DATE_ONLY */
	validFrom?: Nullable<string>;
	control: UseFormReturn<MaterialsFormInput, unknown, MaterialsFormOutput>['control'];
	readonlyFieldClassName?: string;
	getValues: UseFormReturn<MaterialsFormInput, unknown, MaterialsFormOutput>['getValues'];
	unregister: UseFormReturn<MaterialsFormInput, unknown, MaterialsFormOutput>['unregister'];
	onChange: (field: OwnProps['name']) => void;
}

type Props = OwnProps;

const RateInput: React.FC<Props> = (props) => {
	const {
		rate,
		rateData,
		containerClassName,
		fieldsLength,
		readonly,
		rhfId,
		name,
		displayOnly,
		validFrom,
		control,
		readonlyFieldClassName,
		getValues,
		unregister,
		onChange,
	} = props;

	const editingCellName = useEditingTableCellName();

	const defaultValue = rateData[rate] ?? rateData[RATE_TO_COMPANY_RATE_VALUE[rate]];

	const rateSource = rateData[rate] === null ? MaterialCostSource.COMPANY : MaterialCostSource.COMPANY_MATERIAL;

	const handleClick = React.useCallback(() => {
		if (readonly) {
			return;
		}
		editingCellName.setEditingTableCellName(name);
	}, [editingCellName, name, readonly]);

	const prefixGlyphs = React.useMemo<GlyphItem[]>(() => {
		const glyphs: GlyphItem[] = [{
			type: 'node',
			value: <MaterialRateSource rateSource={rateSource} />,
		}];

		if (validFrom) {
			glyphs.push(createValidFromGlyph(validFrom));
		}

		return glyphs;
	}, [rateSource, validFrom]);

	return (
		<div className={containerClassName} onClick={handleClick}>
			{displayOnly ? (
				<DisplayCell
					elementKey={rhfId}
					name={name}
					prefixGlyphs={prefixGlyphs}
					readonly={true}
					type="percent"
					value={defaultValue}
				/>
			) : (
				<InputCellRenderer
					control={control}
					defaultValue={defaultValue.toString()}
					fieldsLength={fieldsLength}
					getValues={getValues}
					name={name}
					onChange={onChange}
					prefixGlyphs={prefixGlyphs}
					readonly={readonly}
					readonlyFieldClassName={readonlyFieldClassName}
					rhfId={rhfId}
					type="percent"
					unregister={unregister}
				/>
			)}
		</div>
	);
};

export default React.memo(RateInput);
